<template>
  <v-dialog
    :value="value"
    :width="width"
    fullscreen
    transition="dialog-bottom-transition"
    eager
    persistent
    no-click-animation
    :retain-focus="false"
    @input="$emit('input', $event)"
    @keydown.esc="onClose"
  >
    <!-- Fullscreen Modal -->
    <div
      :style="{height: '100%'}"
      class="sn-modal-fs sn-background-gray-01 d-flex"
      data-cy="nav-dialog-modal"
    >
      <v-container
        class="fixed text-wrap"
        :style="{width: '25%'}"
      >
        <v-row>
          <div
            class="modal-title-button mt-6 ml-12"
            @click="onClose"
          >
            <v-icon data-cy="exit-modal-btn">
              $snCircleArrowLeft
            </v-icon>
            <span>Exit</span>
          </div>
        </v-row>
        <v-row
          no-gutters
          class="pl-5 my-7"
        >
          <v-col cols="12">
            <div class="modal-title mr-6 ml-6 mb-10">
              <h1
                class="sn-h1"
                data-cy="nav-dialog-modal-title"
              >
                {{ header }}
              </h1>
            </div>
            <v-tabs
              :key="tabRenderKey"
              class="pl-2"
              :value="tab"
              vertical
              hide-slider
              background-color="transparent"
              active-class="selected-tab"
              @change="handleTabChange"
            >
              <template
                v-for="(tab, index) in tabs"
              >
                <v-tab
                  :key="`tab-${index}`"
                  :data-cy="`tab-${index}`"
                  :ripple="false"
                >
                  {{ tab.name }}
                </v-tab>

                <template v-if="tab.children != null && tab.children.length > 0">
                  <v-tab
                    v-for="(child, childIndex) in tab.children"
                    :key="`tab-${index}-${childIndex}`"
                    :data-cy="`tab-${index}-${childIndex}`"
                    :ripple="false"
                    class="nested-tab"
                  >
                    {{ child.name }}
                  </v-tab>
                </template>
              </template>
            </v-tabs>
          </v-col>
        </v-row>
      </v-container>
      <v-container
        ref="scrollContainer"
        fluid
        class="my-0 overflow-y-auto"
      >
        <v-row>
          <v-col
            cols="12"
            class="px-14 py-2"
          >
            <v-tabs-items v-model="tab">
              <v-tab-item
                v-for="(_tab, index) in flatTabs"
                :key="`tab-item-${index}`"
                :transition="false"
                :reverse-transition="false"
              >
                <v-card
                  class="sn-body-1 rounded-lg my-16 pa-8 nav-dialog-card"
                >
                  <slot
                    v-if="activeTab.baseName"
                    name="tabHeader"
                  >
                    <div>
                      <v-row class="mb-12 mx-0">
                        <v-flex class="md12 sm12 xs12 user-column">
                          <div class="avatar-and-name">
                            <div
                              v-if="activeTab.icon != null"
                              class="rounded-circle illustration-container mr-4"
                            >
                              <sn-illustration
                                :illustration="activeTab.icon"
                                size="xs"
                              />
                            </div>

                            <div class="name-updated">
                              <h2
                                class="sn-h3"
                                data-cy="profile-info-title"
                              >
                                {{ activeTab.baseName }}
                              </h2>

                              <span
                                v-if="tabSubtitle != null && tabSubtitle.length > 0"
                                class="tab-subtitle"
                              >
                                {{ tabSubtitle }}
                              </span>
                            </div>
                          </div>
                        </v-flex>
                      </v-row>
                    </div>
                  </slot>

                  <slot name="tabBody" />

                  <sn-btn
                    v-if="!hidePrimaryBtn && primaryBtnText != null"
                    style="margin-top: 10px"
                    :text="primaryBtnText"
                    type="primary"
                    size="large"
                    :disabled="primaryBtnDisabled"
                    :loading="primaryBtnLoading"
                    @click="handlePrimaryClick"
                  />
                </v-card>
              </v-tab-item>
            </v-tabs-items>
          </v-col>
        </v-row>
        <v-row class="justify-center">
          <v-col
            cols="12"
            class="text-center"
            style="position: relative; z-index: 0;"
          >
            <sr-partner-footer />
          </v-col>
        </v-row>
      </v-container>
    </div>
  </v-dialog>
</template>

<script>
import srPartnerFooter from './srPartnerFooter'
import { mapMutations } from 'vuex'

export default {
  name: 'SrNavDialog',

  components: {
    srPartnerFooter
  },
  props: {
    tabs: {
      type: Array,
      required: true,
      default: () => []
    },
    header: {
      type: String,
      default: '',
      required: true
    },
    tabSubtitle: {
      type: String,
      default: '',
      required: false
    },
    value: {
      type: Boolean,
      default: false,
      required: false
    },
    currentTab: {
      type: Number,
      default: 0,
      required: false
    },
    primaryBtnText: {
      type: String,
      default: null,
      required: false
    },
    primaryBtnDisabled: {
      type: Boolean,
      default: false,
      required: false
    },
    primaryBtnLoading: {
      type: Boolean,
      default: false,
      required: false
    },
    hidePrimaryBtn: {
      type: Boolean,
      default: false,
      required: false
    }
  },

  data () {
    return {
      dialog: this.value,
      tab: this.currentTab,
      user: null,
      lastLoginAt: null,
      profilePictureUrl: '',
      roleEnvironments: {},
      hasUserInfoChanges: false,
      hasPasswordChanges: false,
      toggleDiscardChangesModalVisible: false,
      passwordKey: 100,
      tabRenderKey: 0
    }
  },

  computed: {
    width () {
      return this.$vuetify.breakpoint.lgAndUp ? '70%' : '80%'
    },
    flatTabs () {
      let flat = []
      this.tabs.forEach((tab) => {
        flat.push(tab)
        if (tab.children != null) {
          flat = flat.concat(tab.children)
        }
      })
      return flat
    },
    activeTab () {
      return this.flatTabs[this.tab]
    }
  },

  watch: {
    dialog (value, oldValue) {
      if (value) {
        // disable root scrollbar when modal is opened
        document.querySelector('html').style.overflowY = 'hidden'
      } else {
        // re-enable the root scrollbar when modal is closed
        document.querySelector('html').style.overflowY = 'auto'
        // ensure that the scroll top of the right container is reset on dialog close
        this.$refs.scrollContainer.scrollTop = 0
      }
      if (value !== oldValue) {
        this.$emit('input', value)
      }
    },
    value (val) {
      this.dialog = val
    },
    currentTab (val) {
      this.tab = val
    },
    tab (val) {
      this.$emit('update:currentTab', val)
    },
    tabs: {
      handler () {
        this.tabRenderKey++
      },
      deep: true
    }
  },

  methods: {
    ...mapMutations(['resetAppTimeout']),
    onClose () {
      this.$emit('close')
    },
    handleTabChange (val) {
      this.tab = val
      this.resetAppTimeout()
    },
    handlePrimaryClick () {
      this.$emit('primaryClick')
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/snui';

::v-deep {
  .selected-tab {
    font-weight: $font-weight-bold !important;
    font-size: 17px !important;
    line-height: 24px !important;
    text-decoration: underline;
    text-decoration-color: $sn-brand-03;
    text-underline-offset: 6px;
    color: $sn-primary-03;
  }

  .v-tab {
    display: flex;
    justify-content: flex-start;
    font-family: $font-family-primary;
    font-weight: $font-weight-regular;
    font-size: 17px;
    line-height: 24px;
    letter-spacing: normal;
    color: $sn-primary-03 !important;
    padding-bottom: 2px;
    text-transform: none;
  }

  .v-tab:hover {
    opacity: 0.7;
  }

  .theme--light.v-tabs .v-tab--active:hover  {
    opacity: 1;
  }

  .theme--light.v-tabs .v-tab:hover::before {
    opacity: 0 !important;
  }

  .theme--light.v-tabs .v-tab--active:focus::before  {
    opacity: 0 !important;
  }

  .v-tabs--vertical > .v-tabs-bar .v-tab {
    height: 40px;
  }

  .theme--light.v-tabs-items {
    background-color: transparent !important;
  }

  z-index: 1000;
  .sn-modal {
    display: flex;

    &:not(.v-dialog--fullscreen) {
      border-radius: 16px;
    }

    /*** Fullscreen styles ***/
    &.v-dialog--fullscreen .sn-modal-fs {
      &--container {
        max-height: calc(100vh - 120px);
        border-radius: 16px !important;

        .v-data-table__wrapper {
          max-height: calc(100vh - 370px);
          overflow: auto;

          .v-data-table-header .text-start {
            box-shadow: none !important;
          }
        }
      }
      h1 { text-align: center; }
      h2 { text-align: center; }
    }
    /*** End fullscreen styles ***/

    &-title {
      word-break: break-word;
    }

    .sn-h3 {
      font-size: 26px !important;
      font-weight: $font-weight-semi-bold !important;

      .v-icon {
        color: $sn-primary-03;
      }
    }
  }
}

.nav-dialog-card {
  box-shadow: $sn-shadow-depth-1 !important;
}

@media (max-width: 979px) {
  ::v-deep .sn-modal {
    margin: 0 !important;
    height: 100vh;
    max-height: 100% !important;
    border-radius: 0;
    max-width: 100% !important;
    width: 100% !important;
    overflow: auto;

    .v-card {
      max-height: 100% !important;
      height: 100vh;
      min-width: 100% !important;

      .v-card__title {
        width: 100%;

        .v-icon {
          -webkit-appearance: none;
        }
      }
      .v-card__text {
        width: 100%;
      }
    }
  }
}
.modal-title-button {
  font-family: $font-family-primary;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: 0 64px;
  text-align: center;
  align-self: center;
  span {
    font-weight: 600;
  }
}

.modal-title {
  flex-grow: 1;
}

.justify-center {
  justify-content: center !important;
}

.avatar-and-name {
  display: flex;
  position: relative;

  .camera-icon {
    display: flex;
    position: absolute;
    align-items: center;
    justify-content: center;
    background-color: #1f1f1fCC;
    border-radius: 50%;
    border: 2px solid white;
    color: white;
    height: 40px;
    width: 40px;
    left: 72px;
    top: 72px;
    cursor: pointer;
    z-index: 1;
  }

  .name-updated {
    display: flex;
    flex-direction: column;
    justify-content: center;

    .user-updated-at {
      color: $sn-primary-01;
    }

    p {
      margin: 0 0 8px 0;
    }
  }
}

.illustration-container {
  background-color: $sn-brand-01;
  width: 104px;
  height: 104px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.nested-tab {
  margin-left: 16px;
}

.tab-subtitle {
  font-size: 15px;
}
</style>
